import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './app/AppShell';
import { ALL_NAV_ITEMS } from './app/navigation';
import { RequireCapability, RequireSession } from './app/ProtectedRoute';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { BookingsPage } from './pages/BookingsPage';
import { DashboardPage } from './pages/DashboardPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { RoomsPage } from './pages/RoomsPage';
import { SecurityPage } from './pages/SecurityPage';
import { SettingsPage } from './pages/SettingsPage';
import { SupportPage } from './pages/SupportPage';
import { VisitorsPage } from './pages/VisitorsPage';

/**
 * Routing.
 *
 * The prototype had none: `setView()` swapped a global and re-rendered, so there
 * were no URLs, no browser history and no way to link to a record. Real routes
 * are a requirement now — FEAT-014 notifications carry deep-links
 * `{view, type, id}` that must resolve to an addressable screen.
 *
 * Every screen except sign-in sits behind `RequireSession`. Screens that need a
 * capability declare it, and the same capability is enforced server-side on
 * every request the screen makes.
 */
export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/*
        Reached from an email, by someone who by definition cannot sign in. They
        must sit outside `RequireSession`: `path="*"` below is inside it, so
        until these existed `/reset-password?token=…` redirected to `/login` and
        threw the token away — `RequireSession` stores only `location.pathname`.
        Every reset link the platform ever sent was dead on arrival.
      */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<SetPasswordPage mode="reset" />} />
      <Route path="/accept-invitation" element={<SetPasswordPage mode="invitation" />} />

      <Route element={<RequireSession />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/bookings" element={<BookingsPage />} />

          <Route element={<RequireCapability capability="Visitors.Manage" />}>
            <Route path="/visitors" element={<VisitorsPage />} />
          </Route>

          <Route element={<RequireCapability capability="Companies.Manage" />}>
            <Route path="/companies" element={<CompaniesPage />} />
          </Route>

          <Route element={<RequireCapability capability="Employees.View" />}>
            <Route path="/employees" element={<EmployeesPage />} />
          </Route>

          <Route element={<RequireCapability capability="Rooms.Manage" />}>
            <Route path="/rooms" element={<RoomsPage />} />
          </Route>

          <Route element={<RequireCapability capability="Audit.Read" />}>
            <Route path="/security" element={<SecurityPage />} />
          </Route>

          {/*
            No capability. The screen's own calendar section is every employee's
            own connection to give (ADR 0006); the platform-settings section
            inside it is what checks `Settings.Manage`.
          */}
          <Route path="/settings" element={<SettingsPage />} />

          {ALL_NAV_ITEMS.filter(
            (item) =>
              ![
                '/dashboard',
                '/support',
                '/bookings',
                '/visitors',
                '/companies',
                '/employees',
                '/rooms',
                '/security',
                '/settings',
              ].includes(item.path),
          ).map((item) =>
            item.capability ? (
              <Route key={item.path} element={<RequireCapability capability={item.capability} />}>
                <Route
                  path={item.path}
                  element={<PlaceholderPage titleKey={item.key} icon={item.icon} />}
                />
              </Route>
            ) : (
              <Route
                key={item.path}
                path={item.path}
                element={<PlaceholderPage titleKey={item.key} icon={item.icon} />}
              />
            ),
          )}

          {/* An unknown path falls back to the Dashboard, which every role can see. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
